import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  User,
  UILanguage,
  LearningLanguage,
  CEFRLevel
} from '../types'
import { translations, defaultLanguage, detectBrowserLanguage } from '../lib/i18n'

// Multilingual text helper type
interface LocalText {
  ko?: string
  id?: string
  en: string
  [key: string]: string | undefined
}

// ============================================
// Auth Store
// ============================================
interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isOnboarded: boolean
  _hasHydrated: boolean

  setUser: (user: User | null) => void
  setOnboarded: (value: boolean) => void
  logout: () => void
  setHasHydrated: (state: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isOnboarded: false,
      _hasHydrated: false,

      setUser: (user) => set({
        user,
        isAuthenticated: !!user,
        isLoading: false
      }),

      setOnboarded: (value) => set({ isOnboarded: value }),

      logout: () => {
        if (typeof window !== 'undefined') {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
        }
        set({
          user: null,
          isAuthenticated: false,
          isOnboarded: false
        })
      },

      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'speakflow-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        isOnboarded: state.isOnboarded
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

// ============================================
// UI Store (Enhanced with t() and lt() helpers)
// ============================================

// Detect initial language from browser
const getInitialLanguage = (): UILanguage => {
  if (typeof window === 'undefined') return defaultLanguage
  const stored = localStorage.getItem('speakflow-ui')
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      if (parsed.state?.language) return parsed.state.language
    } catch {
      // Ignore parse errors
    }
  }
  return detectBrowserLanguage()
}

interface UIState {
  language: UILanguage
  soundEnabled: boolean
  darkMode: boolean
  _hasHydrated: boolean

  setLanguage: (lang: UILanguage) => void
  setSoundEnabled: (enabled: boolean) => void
  setDarkMode: (enabled: boolean) => void
  setHasHydrated: (state: boolean) => void

  // Translation helpers
  t: (key: string, params?: Record<string, string | number>) => string
  lt: (texts: LocalText) => string
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      language: getInitialLanguage(),
      soundEnabled: true,
      darkMode: false,
      _hasHydrated: false,

      setLanguage: (language) => set({ language }),
      setSoundEnabled: (soundEnabled) => set({ soundEnabled }),
      setDarkMode: (darkMode) => set({ darkMode }),
      setHasHydrated: (state) => set({ _hasHydrated: state }),

      // Translation function using key
      t: (key, params) => {
        const { language } = get()
        const trans = translations[language] || translations.en
        let text = trans[key] || translations.en[key] || key

        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            text = text.replace(`{${k}}`, String(v))
          })
        }

        return text
      },

      // Local text helper: lt({ ko: '한국어', id: 'Indonesian', en: 'English', zh: '中文', ja: '日本語', es: 'Español', vi: 'Tiếng Việt', th: 'ไทย', fr: 'Français', de: 'Deutsch', pt: 'Português', hi: 'हिन्दी', bn: 'বাংলা', tl: 'Filipino' })
      lt: (texts) => {
        const { language } = get()
        return texts[language] || texts.en
      },
    }),
    {
      name: 'speakflow-ui',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ language: state.language, soundEnabled: state.soundEnabled, darkMode: state.darkMode }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

// ============================================
// Learning Store
// ============================================
interface LearningState {
  // 선택한 학습 언어들
  selectedLanguages: LearningLanguage[]
  currentLanguage: LearningLanguage | null

  // 현재 학습 세션
  currentCourseId: string | null
  currentLessonId: string | null
  sessionXp: number

  // Actions
  selectLanguages: (languages: LearningLanguage[]) => void
  setCurrentLanguage: (lang: LearningLanguage) => void
  startLesson: (courseId: string, lessonId: string) => void
  endLesson: () => void
  addSessionXp: (xp: number) => void
}

export const useLearningStore = create<LearningState>()(
  persist(
    (set) => ({
      selectedLanguages: [],
      currentLanguage: null,
      currentCourseId: null,
      currentLessonId: null,
      sessionXp: 0,

      selectLanguages: (languages) => set({
        selectedLanguages: languages,
        currentLanguage: languages[0] || null
      }),

      setCurrentLanguage: (lang) => set({ currentLanguage: lang }),

      startLesson: (courseId, lessonId) => set({
        currentCourseId: courseId,
        currentLessonId: lessonId,
        sessionXp: 0
      }),

      endLesson: () => set({
        currentCourseId: null,
        currentLessonId: null,
        sessionXp: 0
      }),

      addSessionXp: (xp) => set((state) => ({
        sessionXp: state.sessionXp + xp
      })),
    }),
    {
      name: 'speakflow-learning',
      partialize: (state) => ({
        selectedLanguages: state.selectedLanguages,
        currentLanguage: state.currentLanguage
      }),
    }
  )
)

// ============================================
// XP & Level Calculation Helpers
// ============================================
export const calculateLevel = (xp: number): number => {
  let level = 1
  let totalXpNeeded = 0
  while (totalXpNeeded + level * 100 <= xp) {
    totalXpNeeded += level * 100
    level++
  }
  return level
}

export const getXpForNextLevel = (level: number): number => level * 100

export const getCurrentLevelXp = (xp: number): number => {
  let level = 1
  let totalXpNeeded = 0
  while (totalXpNeeded + level * 100 <= xp) {
    totalXpNeeded += level * 100
    level++
  }
  return xp - totalXpNeeded
}

const getTodayString = () => new Date().toISOString().split('T')[0]

// Badge definitions
export const BADGES = [
  { id: 'first_lesson', name: { en: 'First Steps', ko: '첫 걸음', id: 'Langkah Pertama' }, description: { en: 'Complete your first lesson', ko: '첫 레슨 완료', id: 'Selesaikan pelajaran pertama' } },
  { id: 'streak_7', name: { en: 'Week Warrior', ko: '일주일 전사', id: 'Pejuang Mingguan' }, description: { en: '7 day streak', ko: '7일 연속', id: 'Streak 7 hari' } },
  { id: 'streak_30', name: { en: 'Monthly Master', ko: '한 달 마스터', id: 'Master Bulanan' }, description: { en: '30 day streak', ko: '30일 연속', id: 'Streak 30 hari' } },
  { id: 'xp_1000', name: { en: 'XP Hunter', ko: 'XP 사냥꾼', id: 'Pemburu XP' }, description: { en: 'Earn 1000 XP', ko: '1000 XP 획득', id: 'Dapatkan 1000 XP' } },
  { id: 'xp_5000', name: { en: 'XP Master', ko: 'XP 마스터', id: 'Master XP' }, description: { en: 'Earn 5000 XP', ko: '5000 XP 획득', id: 'Dapatkan 5000 XP' } },
  { id: 'perfect_lesson', name: { en: 'Perfectionist', ko: '완벽주의자', id: 'Perfeksionis' }, description: { en: 'Get 100% on a lesson', ko: '레슨에서 100% 달성', id: 'Dapat 100% di pelajaran' } },
  { id: 'level_5', name: { en: 'Rising Star', ko: '떠오르는 별', id: 'Bintang Naik' }, description: { en: 'Reach level 5', ko: '레벨 5 달성', id: 'Capai level 5' } },
  { id: 'level_10', name: { en: 'Language Explorer', ko: '언어 탐험가', id: 'Penjelajah Bahasa' }, description: { en: 'Reach level 10', ko: '레벨 10 달성', id: 'Capai level 10' } },
] as const

// Free tier limits
export const FREE_TIER_LIMITS = {
  dailyXpLimit: 50,
  previewLessonsPerCourse: 2,
  freeCourseSlugs: ['hangul-basics', 'hangul-v2', 'greetings'],
}

// ============================================
// Gamification Store (Enhanced)
// ============================================
interface GamificationState {
  xp: number
  level: number
  streak: {
    current: number
    longest: number
    lastStudyDate: string | null
  }
  badges: Array<{ id: string; earnedAt: string }>
  dailyGoal: number
  todayXp: number
  _hasHydrated: boolean

  // Actions
  addXp: (amount: number) => void
  updateStreak: () => void
  earnBadge: (badgeId: string) => void
  hasBadge: (badgeId: string) => boolean
  setDailyGoal: (goal: number) => void
  resetTodayXp: () => void
  setHasHydrated: (state: boolean) => void

  // Firestore sync setters
  setXp: (xp: number) => void
  setLevel: (level: number) => void
  setStreak: (streak: { current: number; longest: number; lastStudyDate: string | null }) => void
  setBadges: (badges: Array<{ id: string; earnedAt: string }>) => void
  setTodayXp: (todayXp: number) => void
}

export const useGamificationStore = create<GamificationState>()(
  persist(
    (set, get) => ({
      xp: 0,
      level: 1,
      streak: {
        current: 0,
        longest: 0,
        lastStudyDate: null
      },
      badges: [],
      dailyGoal: 50, // 50 XP default
      todayXp: 0,
      _hasHydrated: false,

      addXp: (amount) => set((state) => {
        const newXp = state.xp + amount
        const newLevel = calculateLevel(newXp)
        const today = getTodayString()
        const newTodayXp = state.streak.lastStudyDate === today
          ? state.todayXp + amount
          : amount

        return {
          xp: newXp,
          level: newLevel,
          todayXp: newTodayXp,
        }
      }),

      updateStreak: () => set((state) => {
        const today = getTodayString()
        const lastDate = state.streak.lastStudyDate

        if (lastDate === today) {
          return state
        }

        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const yesterdayString = yesterday.toISOString().split('T')[0]

        let newCurrent = 1
        if (lastDate === yesterdayString) {
          newCurrent = state.streak.current + 1
        }

        const newLongest = Math.max(state.streak.longest, newCurrent)

        return {
          streak: {
            current: newCurrent,
            longest: newLongest,
            lastStudyDate: today,
          },
          todayXp: 0,
        }
      }),

      earnBadge: (badgeId) => set((state) => {
        if (state.badges.some(b => b.id === badgeId)) {
          return state
        }
        return {
          badges: [...state.badges, { id: badgeId, earnedAt: new Date().toISOString() }]
        }
      }),

      hasBadge: (badgeId) => get().badges.some(b => b.id === badgeId),

      setDailyGoal: (dailyGoal) => set({ dailyGoal }),

      resetTodayXp: () => set({ todayXp: 0 }),

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      // Firestore sync setters
      setXp: (xp) => set({ xp, level: calculateLevel(xp) }),
      setLevel: (level) => set({ level }),
      setStreak: (streak) => set({ streak }),
      setBadges: (badges) => set({ badges }),
      setTodayXp: (todayXp) => set({ todayXp }),
    }),
    {
      name: 'speakflow-gamification',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        streak: state.streak,
        xp: state.xp,
        level: state.level,
        todayXp: state.todayXp,
        dailyGoal: state.dailyGoal,
        badges: state.badges,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

// ============================================
// Progress Store (각 언어별 진도)
// ============================================
interface ProgressState {
  progress: Record<LearningLanguage, {
    level: CEFRLevel
    completedLessons: string[]
    lessonScores: Record<string, { score: number; completedAt: string }>
  }>

  // Global completed lessons (all languages combined)
  completedLessons: string[]

  // Actions
  completeLesson: (lang: LearningLanguage, lessonId: string, score: number) => void
  setLevel: (lang: LearningLanguage, level: CEFRLevel) => void
  isLessonCompletedByLang: (lang: LearningLanguage, lessonId: string) => boolean
  // Simplified helpers (for Korean default)
  isLessonCompleted: (lessonId: string) => boolean
  getLessonScore: (lessonId: string) => number | null
}

const defaultProgress = {
  level: 'A1' as CEFRLevel,
  completedLessons: [],
  lessonScores: {}
}

export const useProgressStore = create<ProgressState>()(
  persist(
    (set, get) => ({
      progress: {
        ko: { ...defaultProgress },
        en: { ...defaultProgress },
        ja: { ...defaultProgress },
        zh: { ...defaultProgress },
        es: { ...defaultProgress },
        id: { ...defaultProgress },
        vi: { ...defaultProgress },
        fr: { ...defaultProgress },
      },

      // Computed: all completed lessons across languages
      get completedLessons() {
        const state = get()
        const allCompleted = new Set<string>()
        Object.values(state.progress).forEach(p => {
          p.completedLessons.forEach(id => allCompleted.add(id))
        })
        return Array.from(allCompleted)
      },

      completeLesson: (lang, lessonId, score) => {
        const current = get().progress[lang]
        if (!current.completedLessons.includes(lessonId)) {
          set({
            progress: {
              ...get().progress,
              [lang]: {
                ...current,
                completedLessons: [...current.completedLessons, lessonId],
                lessonScores: {
                  ...current.lessonScores,
                  [lessonId]: {
                    score,
                    completedAt: new Date().toISOString()
                  }
                }
              }
            }
          })
        }
      },

      setLevel: (lang, level) => {
        set({
          progress: {
            ...get().progress,
            [lang]: {
              ...get().progress[lang],
              level
            }
          }
        })
      },

      isLessonCompletedByLang: (lang, lessonId) => {
        return get().progress[lang].completedLessons.includes(lessonId)
      },

      // Simplified: check any language (default to Korean)
      isLessonCompleted: (lessonId) => {
        const state = get()
        return Object.values(state.progress).some(p => p.completedLessons.includes(lessonId))
      },

      // Get lesson score (check all languages)
      getLessonScore: (lessonId) => {
        const state = get()
        for (const p of Object.values(state.progress)) {
          if (p.lessonScores[lessonId]) {
            return p.lessonScores[lessonId].score
          }
        }
        return null
      },
    }),
    {
      name: 'speakflow-progress',
    }
  )
)

// ============================================
// Subscription Store (Premium Access)
// ============================================
interface SubscriptionState {
  isPremium: boolean
  subscriptionType: 'free' | 'monthly' | 'yearly' | 'lifetime'
  subscriptionEndDate: string | null
  _hasHydrated: boolean

  // Actions
  setPremium: (isPremium: boolean) => void
  setSubscription: (type: 'free' | 'monthly' | 'yearly' | 'lifetime', endDate?: string) => void
  checkAccess: (courseSlug: string, lessonIndex: number) => boolean
  setHasHydrated: (state: boolean) => void
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      isPremium: false,
      subscriptionType: 'free',
      subscriptionEndDate: null,
      _hasHydrated: false,

      setPremium: (isPremium) => set({ isPremium }),

      setSubscription: (type, endDate) => set({
        subscriptionType: type,
        isPremium: type !== 'free',
        subscriptionEndDate: endDate || null,
      }),

      // Check if user can access a specific lesson
      checkAccess: (courseSlug, lessonIndex) => {
        const { isPremium } = get()
        // Premium users have full access
        if (isPremium) return true
        // Free courses are fully accessible
        if (FREE_TIER_LIMITS.freeCourseSlugs.includes(courseSlug)) return true
        // Free tier: only first N lessons
        return lessonIndex < FREE_TIER_LIMITS.previewLessonsPerCourse
      },

      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: 'speakflow-subscription',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isPremium: state.isPremium,
        subscriptionType: state.subscriptionType,
        subscriptionEndDate: state.subscriptionEndDate,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

// ============================================
// Onboarding Store
// ============================================
interface OnboardingState {
  step: number
  nativeLanguage: UILanguage | null
  selectedLearningLanguages: LearningLanguage[]
  dailyGoal: number

  setStep: (step: number) => void
  setNativeLanguage: (lang: UILanguage) => void
  toggleLearningLanguage: (lang: LearningLanguage) => void
  setDailyGoal: (minutes: number) => void
  reset: () => void
}

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
  step: 0,
  nativeLanguage: null,
  selectedLearningLanguages: [],
  dailyGoal: 10,

  setStep: (step) => set({ step }),

  setNativeLanguage: (lang) => set({ nativeLanguage: lang }),

  toggleLearningLanguage: (lang) => {
    const current = get().selectedLearningLanguages
    if (current.includes(lang)) {
      set({ selectedLearningLanguages: current.filter(l => l !== lang) })
    } else {
      set({ selectedLearningLanguages: [...current, lang] })
    }
  },

  setDailyGoal: (minutes) => set({ dailyGoal: minutes }),

  reset: () => set({
    step: 0,
    nativeLanguage: null,
    selectedLearningLanguages: [],
    dailyGoal: 10
  }),
}))
